import { defineStore } from 'pinia'
import axios from 'axios'
import router from '@/routers'
import { useUsersStore } from '@/stores/users'

function groupKey(group) {
    const key = group?.id ?? group?.external_id
    return key == null ? '' : String(key)
}

function userParticipates(group, userId) {
    if (!group || userId == null || userId === '' || !Array.isArray(group.users)) return false

    return group.users.some((user) =>
        groupUserId(user) === Number(userId),
    )
}

function groupUserId(user) {
    return Number(user?.user_id ?? user?.user?.id ?? user?.id)
}

function normalizeGroupMuted(group, userId) {
    if (!group) return group

    group.muted = group.muted === true || Number(group.muted) === 1
    if (!Array.isArray(group.users)) return group

    const currentUser = group.users.find((user) => groupUserId(user) === Number(userId))
    group.user_muted = currentUser?.muted === true || Number(currentUser?.muted) === 1

    return group
}

function normalizeIds(ids) {
    return [...new Set((Array.isArray(ids) ? ids : [ids]).map(Number).filter(Boolean))]
}

function mergeMessageMentions(incomingMentions, localMentions) {
    const incoming = Array.isArray(incomingMentions) ? incomingMentions : []
    const local = Array.isArray(localMentions) ? localMentions : []
    if (!local.length) return incoming
    if (!incoming.length) return local

    const lidKey = (mention) => {
        const lid = String(mention?.lid ?? '').trim()
        return lid.toLowerCase() === 'all' ? 'all' : lid.replace(/\D/g, '')
    }
    const localByLid = new Map(local.map(mention => [lidKey(mention), mention]))
    const mergedLids = new Set()

    const merged = incoming.map((mention) => {
        const lid = lidKey(mention)
        const selectedMention = localByLid.get(lid)
        if (!selectedMention) return mention

        mergedLids.add(lid)
        return {
            ...mention,
            name: selectedMention.name || mention.name,
        }
    })

    local.forEach((mention) => {
        if (!mergedLids.has(lidKey(mention))) merged.push(mention)
    })

    return merged
}

function mergeGroup(previous, incoming) {
    const users = Array.isArray(incoming?.users) ? incoming.users : previous.users
    Object.assign(previous, incoming)
    if (users !== undefined) previous.users = users
    return previous
}

function groupActivityTimestamp(group) {
    const value = group?.last_message?.timestamp
        ?? group?.lastMessage?.timestamp
        ?? group?.updated_at

    if (!value) return 0

    const numeric = Number(value)
    if (Number.isFinite(numeric)) {
        return String(Math.trunc(numeric)).length <= 10 ? numeric * 1000 : numeric
    }

    const parsed = new Date(value).getTime()
    return Number.isNaN(parsed) ? 0 : parsed
}

export function useChatGroups() {
    return defineStore('groups', {
        state: () => ({
            // Grupos em que o atendente participa. Lista de /v2/chat/grupos.
            groups: [],
            // Catálogo completo, equivalente ao audit dos atendimentos.
            dashboardGroups: [],
            messages: {},
            activeGroup: null,
            loading: false,
            loadingMore: false,
            loadingMessages: false,
            membershipLoading: {},
            managingUsers: false,
            refreshingGroup: false,
            photoRefreshLoading: {},
            muteLoading: {},
            muteAllLoading: {},
            error: null,
            pagination: {
                currentPage: 0,
                lastPage: 1,
                perPage: 20,
                total: 0,
                next: null,
            },
        }),

        getters: {
            count: (state) => state.pagination.total || state.dashboardGroups.length,
            hasMoreGroups: (state) =>
                state.pagination.currentPage < state.pagination.lastPage
                || state.pagination.next !== null,
            /**
             * Todos os grupos da dashboard, no mesmo papel de attendancesInDashboard.
             * A lista do chat fica em `groups` e só inclui grupos do atendente.
             */
            groupsInDashboard: (state) => {
                const ids = new Set(state.dashboardGroups.map((item) => item.id))
                const onlyChat = state.groups.filter((item) => item.id != null && !ids.has(item.id))

                const list = onlyChat.length
                    ? [...onlyChat, ...state.dashboardGroups]
                    : [...state.dashboardGroups]

                return list.sort((a, b) => groupActivityTimestamp(b) - groupActivityTimestamp(a))
            },
            isGroupMember: (state) => (groupId, userId) => {
                const group = state.dashboardGroups.find((item) => Number(item.id) === Number(groupId))
                    || state.groups.find((item) => Number(item.id) === Number(groupId))

                return userParticipates(group, userId)
            },
            byId: (state) => (id) =>
                state.dashboardGroups.find((group) => Number(group.id) === Number(id))
                || state.groups.find((group) => Number(group.id) === Number(id)),
            messagesOf: (state) => (groupId) => state.messages[groupId] || [],
        },

        actions: {
            setBaseUrl() {
                axios.defaults.baseURL =
                    import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
            },

            async fetchGroups({ page = 1, perPage = 20 } = {}) {
                const reset = page === 1
                if (this.loading || this.loadingMore) return
                if (!reset && !this.hasMoreGroups) return

                this.setBaseUrl()
                this.loading = reset
                this.loadingMore = !reset
                this.error = null

                try {
                    const { data } = await axios.get('/chat/groups', {
                        params: {
                            page,
                            per_page: Math.min(Number(perPage) || 20, 100),
                        },
                    })

                    const items = Array.isArray(data) ? data : (data?.data || [])
                    this.absorbGroups(items, reset)

                    const meta = data?.meta || {}
                    const links = data?.links || {}
                    this.pagination = {
                        currentPage: Number(meta.current_page || page),
                        lastPage: Number(meta.last_page || page),
                        perPage: Number(meta.per_page || perPage),
                        total: Number(meta.total ?? this.dashboardGroups.length),
                        next: links.next ?? meta.next ?? null,
                    }

                    if (this.activeGroup?.id != null) {
                        this.activeGroup = this.byId(this.activeGroup.id) || this.activeGroup
                    }
                } catch (e) {
                    this.error = e?.response?.data?.message || e?.message || 'Erro ao carregar grupos'
                } finally {
                    this.loading = false
                    this.loadingMore = false
                }
            },

            absorbGroups(items, reset = false) {
                const unique = new Map()
                const source = reset ? [] : this.dashboardGroups

                source.forEach((group) => {
                    const key = groupKey(group)
                    if (key) unique.set(key, group)
                })

                items.forEach((group) => {
                    const key = groupKey(group)
                    if (!key) return

                    const previous = unique.get(key)
                    unique.set(key, previous ? mergeGroup(previous, group) : group)
                })

                this.dashboardGroups = [...unique.values()]
                this.syncChatGroups()
            },

            syncChatGroups() {
                const meId = useUsersStore().me?.id

                this.dashboardGroups.forEach((group) => normalizeGroupMuted(group, meId))
                this.groups = this.dashboardGroups.filter((group) => userParticipates(group, meId))
            },

            findGroup(groupOrId, externalId = null) {
                const id = groupOrId?.id ?? groupOrId
                const external = groupOrId?.external_id ?? externalId

                return this.dashboardGroups.find((item) =>
                    (id != null && Number(item.id) === Number(id))
                    || (external != null && item.external_id === external),
                ) || this.groups.find((item) =>
                    (id != null && Number(item.id) === Number(id))
                    || (external != null && item.external_id === external),
                )
            },

            async loadMoreGroups() {
                if (!this.hasMoreGroups || this.loading || this.loadingMore) return

                return this.fetchGroups({
                    page: this.pagination.currentPage + 1,
                    perPage: this.pagination.perPage,
                })
            },

            async fetchMessages(groupId) {
                this.setBaseUrl()
                this.loadingMessages = true

                try {
                    const { data } = await axios.get(`/chat/groups/${groupId}/messages`)
                    this.messages[groupId] = Array.isArray(data) ? data : []
                } catch (e) {
                    this.error = e?.response?.data?.message || e?.message || 'Erro ao carregar mensagens'
                } finally {
                    this.loadingMessages = false
                }
            },

            async sendGroupAction(action, data) {
                const { sendWebSocketMessage } = await import('@/composables/chat/useWebSocket')
                sendWebSocketMessage({
                    type: 'chat',
                    action,
                    data,
                    force_return: true,
                })
            },

            membershipPayload(groupId) {
                const me = useUsersStore().me || {}
                const group = this.byId(groupId) || this.activeGroup

                return {
                    group_id: Number(groupId),
                    user_id: Number(me.id),
                    message_by: Number(me.id),
                    business_id: Number(group?.business_id || me.business_id || 0),
                }
            },

            async changeMembership(groupId, action) {
                const payload = this.membershipPayload(groupId)
                if (!payload.group_id || !payload.user_id || this.membershipLoading[groupId]) return

                this.membershipLoading[groupId] = true
                try {
                    await this.sendGroupAction(action, payload)
                } catch (error) {
                    this.membershipLoading[groupId] = false
                    this.error = error?.message || 'Não foi possível atualizar sua participação no grupo'
                }
            },

            join(groupId) {
                return this.changeMembership(groupId, 'group_join')
            },

            leave(groupId) {
                return this.changeMembership(groupId, 'group_leave')
            },

            async manageUsers(groupId, userIds, action) {
                const ids = normalizeIds(userIds)
                const me = useUsersStore().me || {}
                const group = this.byId(groupId) || this.activeGroup
                if (!groupId || !me.id || !ids.length || this.managingUsers) return

                this.managingUsers = true
                try {
                    await this.sendGroupAction(action, {
                        group_id: Number(groupId),
                        user_ids: ids,
                        message_by: Number(me.id),
                        business_id: Number(group?.business_id || me.business_id || 0),
                    })
                } catch (error) {
                    this.managingUsers = false
                    this.error = error?.message || 'Não foi possível atualizar os atendentes do grupo'
                }
            },

            addUsers(groupId, userIds) {
                return this.manageUsers(groupId, userIds, 'group_add_user')
            },

            removeUsers(groupId, userIds) {
                return this.manageUsers(groupId, userIds, 'group_remove_user')
            },

            async refreshGroup(groupId) {
                if (!groupId || this.refreshingGroup) return

                this.refreshingGroup = true
                try {
                    await this.sendGroupAction('group_refresh', {
                        group_id: Number(groupId),
                    })
                } catch (error) {
                    this.refreshingGroup = false
                    this.error = error?.message || 'Não foi possível atualizar o grupo'
                }
            },

            async refreshParticipantPhoto(clientId, channelId) {
                const id = Number(clientId)
                if (!id || !channelId || this.photoRefreshLoading[id]) return

                this.photoRefreshLoading[id] = true
                try {
                    await this.sendGroupAction('client_photo_refresh', {
                        client_id: id,
                        channel_id: Number(channelId),
                    })
                } catch (error) {
                    this.clearPhotoRefreshLoading(id)
                    this.error = error?.message || 'Não foi possível atualizar a foto do participante'
                }
            },

            clearPhotoRefreshLoading(clientId) {
                const id = Number(clientId)
                if (id) {
                    this.photoRefreshLoading[id] = false
                    return
                }

                Object.keys(this.photoRefreshLoading).forEach((key) => {
                    this.photoRefreshLoading[key] = false
                })
            },

            async toggleMute(groupId, muted) {
                const me = useUsersStore().me || {}
                const id = Number(groupId)
                if (!id || !me.id || this.muteLoading[id]) return

                this.muteLoading[id] = true
                try {
                    await this.sendGroupAction('group_mute', {
                        group_id: id,
                        muted: muted === true,
                        message_by: Number(me.id),
                        business_id: Number(this.byId(id)?.business_id || me.business_id || 0),
                    })
                } catch (e) {
                    this.muteLoading[id] = false
                    this.error = e?.message || 'Não foi possível atualizar o silêncio do grupo'
                }
            },

            async toggleMuteAll(groupId, muted) {
                const me = useUsersStore().me || {}
                const id = Number(groupId)
                if (!id || !me.id || this.muteAllLoading[id]) return

                this.muteAllLoading[id] = true
                try {
                    await this.sendGroupAction('group_mute_all', {
                        group_id: id,
                        muted: muted === true,
                        message_by: Number(me.id),
                        business_id: Number(this.byId(id)?.business_id || me.business_id || 0),
                    })
                } catch (e) {
                    this.muteAllLoading[id] = false
                    this.error = e?.message || 'Não foi possível atualizar o silêncio global do grupo'
                }
            },

            async deleteGroup(groupId) {
                this.setBaseUrl()
                await axios.delete(`/chat/groups/${groupId}`)
                this.removeGroup(groupId)
            },

            // Eventos recebidos via WebSocket

            upsertGroup(group) {
                if (!group?.id && !group?.external_id) return

                const existing = this.findGroup(group)
                if (!existing) {
                    this.dashboardGroups.unshift(group)
                    this.pagination.total += 1
                } else {
                    mergeGroup(existing, group)
                }

                this.syncChatGroups()
                const storedGroup = existing || group

                if (Number(this.activeGroup?.id) === Number(storedGroup.id)) {
                    this.activeGroup = storedGroup
                }

                return storedGroup
            },

            receiveGroupMessage(payload) {
                const message = payload?.message || {}
                const group = this.upsertGroup(payload?.group || {})
                if (!group) return

                if (
                    message?.id
                    || message?.temp_id
                    || message?.message_random
                    || payload?.temp_id
                    || payload?.message_random
                ) {
                    const key = group.id
                    const list = this.messages[key] || []
                    const returnedTempId = message.temp_id
                        ?? message.message_random
                        ?? payload?.temp_id
                        ?? payload?.message_random

                    // Reconcile a mensagem otimista enviada pelo próprio atendente (temp_id)
                    if (returnedTempId) {
                        const idx = list.findIndex((m) => String(m.temp_id) === String(returnedTempId))
                        if (idx !== -1) {
                            const optimisticMessage = list[idx]
                            const previewUrl = optimisticMessage?.content
                            const mentions = mergeMessageMentions(
                                message.mentions,
                                optimisticMessage?.mentions,
                            )
                            this.messages[key] = list.map((m, i) => (
                                i === idx
                                    ? {
                                        ...message,
                                        temp_id: returnedTempId,
                                        status: undefined,
                                        mentions,
                                    }
                                    : m
                            ))
                            if (typeof previewUrl === 'string' && previewUrl.startsWith('blob:')) {
                                URL.revokeObjectURL(previewUrl)
                            }
                            this.bumpMessagesCount(group)
                            this.bumpGroupActivity(group, message)
                            return
                        }
                    }

                    const existingIndex = list.findIndex((item) =>
                        (message.id != null && Number(item.id) === Number(message.id))
                        || (
                            message.message_id
                            && String(item.message_id) === String(message.message_id)
                        ),
                    )

                    if (existingIndex !== -1) {
                        this.messages[key] = list.map((item, index) => (
                            index === existingIndex ? { ...item, ...message } : item
                        ))
                        return
                    }

                    this.messages[key] = [...list, message]
                    this.bumpMessagesCount(group)
                    this.bumpGroupActivity(group, message)
                }
            },

            /**
             * Incrementa o contador de mensagens do grupo localmente. O `group`
             * leve do `new_group_message` não traz `messages_count`, então o
             * mantemos atualizado aqui para o card não ficar defasado.
             */
            bumpMessagesCount(group) {
                const current = Number(group?.messages_count)
                if (Number.isFinite(current)) {
                    group.messages_count = current + 1
                }
            },

            /**
             * Atualiza a última atividade do grupo para que ele suba para o topo
             * da lista, igual ao comportamento do chat de clientes.
             */
            bumpGroupActivity(group, message) {
                if (!group) return

                const timestamp = message?.timestamp
                    ?? message?.created_at
                    ?? Math.round(Date.now() / 1000)

                const lastMessage = group.last_message
                    ? group.last_message
                    : (group.last_message = {})

                lastMessage.timestamp = timestamp
                if (message?.content !== undefined) lastMessage.content = message.content
                if (message?.type_content !== undefined) lastMessage.type_content = message.type_content
                if (message?.sender !== undefined) lastMessage.sender = message.sender
            },

            /**
             * Envia uma mensagem para um grupo. A resposta `new_group_message` é
             * reconciliada em `receiveGroupMessage` pelo `temp_id`.
             */
            async sendMessage(groupId, content, options = {}) {
                const me = useUsersStore().me || {}

                const tempId = `${Date.now()}${Math.floor(Math.random() * 900) + 100}`
                const replyContent = options.reply?.content
                const replyMessage = typeof replyContent === 'string' || typeof replyContent === 'number'
                    ? String(replyContent)
                    : (replyContent?.content || replyContent?.text || replyContent?.body || '')

                const optimistic = {
                    id: null,
                    temp_id: tempId,
                    content,
                    type_content: options.type_content || 'text',
                    legend: options.legend || null,
                    type: options.internal ? 'internal' : 'sent',
                    origin: options.internal ? 'internal' : 'chat',
                    message_internal: options.internal === true,
                    timestamp: Math.round(Date.now() / 1000),
                    from_me: true,
                    status: 'sending',
                    extra: options.message_reply
                        ? {
                            type: 'response',
                            data: {
                                uuid: options.message_reply,
                                message: replyMessage,
                                type_content: options.reply?.type_content || 'text',
                            },
                        }
                        : null,
                    reply: options.message_reply
                        ? {
                            uuid: options.message_reply,
                            content: {
                                type: options.reply?.type_content || 'text',
                                content: replyMessage,
                            },
                        }
                        : null,
                    forward: false,
                    mentions: options.mentions || [],
                    sender: {
                        external_id: null,
                        name: [me.first_name, me.last_name].filter(Boolean).join(' ') || null,
                        photo: me.photo || null,
                    },
                }

                const key = Number(groupId)
                const list = this.messages[key] || []
                this.messages[key] = [...list, optimistic]

                try {
                    // Import dinâmico para evitar dependência circular com useWebSocketDispatch.
                    const { sendWebSocketMessage } = await import('@/composables/chat/useWebSocket')

                    sendWebSocketMessage({
                        type: 'chat',
                        action: 'group_send',
                        data: {
                            group_id: Number(groupId),
                            content,
                            type_content: options.type_content || 'text',
                            message_random: tempId,
                            message_by: me.id,
                            message_reply: options.message_reply || null,
                            message_internal: options.internal === true,
                            legend: options.legend || null,
                        },
                        force_return: true,
                    })

                    return tempId
                } catch (e) {
                    this.messages[key] = (this.messages[key] || []).map((message) => (
                        message.temp_id === tempId
                            ? { ...message, status: 'failed' }
                            : message
                    ))
                    this.error = e?.message || 'Erro ao enviar mensagem para o grupo'
                    console.error('[groups] Falha ao enviar mensagem para o grupo:', e)
                }

                return null
            },

            /**
             * Atualiza o ack (sent/received/read) das mensagens de grupo
             * recebido via `message_updated_ack`.
             */
            updateAck(acks) {
                if (!Array.isArray(acks)) return

                acks.forEach((ackItem) => {
                    const groupId = Number(ackItem.group_id)
                    const list = this.messages[groupId]
                    if (!Array.isArray(list) || !ackItem.message_id) return

                    this.messages[groupId] = list.map((message) =>
                        String(message.message_id) === String(ackItem.message_id)
                            ? {
                                ...message,
                                ack: ackItem.ack,
                                ack_tooltip: ackItem.tooltip || message.ack_tooltip,
                            }
                            : message,
                    )
                })
            },

            updateGroupUsers(groupId, userIds, add) {
                const group = this.findGroup(groupId)
                if (!group) {
                    this.fetchGroups()
                    return null
                }

                const ids = normalizeIds(userIds)
                const users = Array.isArray(group.users) ? group.users : []

                if (add) {
                    const existingIds = new Set(users.map(groupUserId))
                    group.users = [
                        ...users,
                        ...ids
                            .filter((id) => !existingIds.has(id))
                            .map((id) => ({ id, user_id: id })),
                    ]
                } else {
                    const removedIds = new Set(ids)
                    group.users = users.filter((user) => !removedIds.has(groupUserId(user)))
                }

                this.syncChatGroups()
                return group
            },

            openGroupAfterJoin(group, userIds) {
                const meId = Number(useUsersStore().me?.id)
                if (!group || !normalizeIds(userIds).includes(meId)) return

                this.activeGroup = group
                this.fetchMessages(group.id)

                if (
                    router.currentRoute.value.name !== 'chat.attendances.groups'
                    || Number(router.currentRoute.value.params.id) !== Number(group.id)
                ) {
                    router.push({
                        name: 'chat.attendances.groups',
                        params: { id: group.id },
                    })
                }
            },

            closeGroupAfterLeave(groupId, userIds) {
                const meId = Number(useUsersStore().me?.id)
                if (!normalizeIds(userIds).includes(meId)) return
                if (Number(this.activeGroup?.id) !== Number(groupId)) return

                this.activeGroup = null
                if (router.currentRoute.value.name === 'chat.attendances.groups') {
                    router.replace({ name: 'chat.attendances.groups' })
                }
            },

            groupJoined(payload) {
                const groupId = Number(payload?.group_id)
                const userId = Number(payload?.user_id)
                this.membershipLoading[groupId] = false

                const group = this.updateGroupUsers(groupId, userId, true)
                this.openGroupAfterJoin(group, userId)
            },

            groupLeft(payload) {
                const groupId = Number(payload?.group_id)
                const userId = Number(payload?.user_id)
                this.membershipLoading[groupId] = false

                this.updateGroupUsers(groupId, userId, false)
                this.closeGroupAfterLeave(groupId, userId)
            },

            groupUsersAdded(payload) {
                const groupId = Number(payload?.group_id)
                const userIds = normalizeIds(payload?.user_ids)
                this.managingUsers = false

                const group = this.updateGroupUsers(groupId, userIds, true)
                this.openGroupAfterJoin(group, userIds)
            },

            groupUsersRemoved(payload) {
                const groupId = Number(payload?.group_id)
                const userIds = normalizeIds(payload?.user_ids)
                this.managingUsers = false

                this.updateGroupUsers(groupId, userIds, false)
                this.closeGroupAfterLeave(groupId, userIds)
            },

            groupRefreshed(payload) {
                this.refreshingGroup = false
                this.upsertGroup(payload?.group || {})
            },

            participantPhotoUpdated(payload) {
                const clientId = Number(payload?.client_id)
                this.clearPhotoRefreshLoading(clientId)
                if (!clientId) return
                const photo = payload?.photo
                    ? `${payload.photo}${payload.photo.includes('?') ? '&' : '?'}v=${Date.now()}`
                    : null

                const updateParticipant = (participant) => {
                    const client = participant?.client || participant
                    const id = Number(participant?.client_id ?? client?.id)
                    if (id !== clientId) return

                    if (participant?.client) participant.client.photo = photo
                    else participant.photo = photo
                }

                const groups = new Set([...this.dashboardGroups, ...this.groups, this.activeGroup].filter(Boolean))
                groups.forEach((group) => {
                    ['clients', 'participants', 'members', 'contacts'].forEach((key) => {
                        if (Array.isArray(group[key])) group[key].forEach(updateParticipant)
                    })
                })

                Object.values(this.messages).flat().forEach((message) => {
                    if (Number(message.client_id ?? message.client?.id) !== clientId) return
                    if (message.client) message.client.photo = photo
                    if (message.sender) message.sender.photo = photo
                })
            },

            groupMuted(payload) {
                const groupId = Number(payload?.group_id)
                const userId = Number(payload?.user_id)
                const muted = payload?.muted === true || Number(payload?.muted) === 1
                if (!groupId) return

                this.muteLoading[groupId] = false

                const groups = new Set([
                    ...this.dashboardGroups,
                    ...this.groups,
                    this.activeGroup,
                ].filter(Boolean))

                groups.forEach((group) => {
                    if (Number(group.id) !== groupId) return

                    group.user_muted = muted
                    const groupUser = Array.isArray(group.users)
                        ? group.users.find((user) => groupUserId(user) === userId)
                        : null
                    if (groupUser) groupUser.muted = muted
                })
            },

            groupMutedAll(payload) {
                const groupId = Number(payload?.group_id)
                const muted = payload?.muted === true || Number(payload?.muted) === 1
                if (!groupId) return

                this.muteAllLoading[groupId] = false

                const groups = new Set([
                    ...this.dashboardGroups,
                    ...this.groups,
                    this.activeGroup,
                ].filter(Boolean))

                groups.forEach((group) => {
                    if (Number(group.id) === groupId) group.muted = muted
                })
            },

            groupActionFailed(message, action, payload = {}) {
                if (['group_join', 'group_joined', 'group_leave', 'group_left'].includes(action)) {
                    const groupId = Number(payload?.group_id)
                    if (groupId) this.membershipLoading[groupId] = false
                }

                if (['group_add_user', 'group_remove_user', 'group_users_added', 'group_users_removed'].includes(action)) {
                    this.managingUsers = false
                }

                if (action === 'group_refresh') {
                    this.refreshingGroup = false
                }

                if (['client_photo_refresh', 'client_photo_updated'].includes(action)) {
                    this.clearPhotoRefreshLoading(
                        payload?.client_id ?? payload?.client?.id ?? payload?.data?.client_id,
                    )
                }

                if (['group_mute', 'group_muted'].includes(action)) {
                    const groupId = Number(payload?.group_id)
                    if (groupId) this.muteLoading[groupId] = false
                }

                if (['group_mute_all', 'group_muted_all'].includes(action)) {
                    const groupId = Number(payload?.group_id)
                    if (groupId) this.muteAllLoading[groupId] = false
                }

                this.error = message || 'Não foi possível atualizar o grupo'
            },

            removeGroup(groupId) {
                const existed = this.dashboardGroups.some((group) => Number(group.id) === Number(groupId))
                    || this.groups.some((group) => Number(group.id) === Number(groupId))

                this.dashboardGroups = this.dashboardGroups.filter((group) => Number(group.id) !== Number(groupId))
                this.groups = this.groups.filter((group) => Number(group.id) !== Number(groupId))
                if (existed) this.pagination.total = Math.max(0, this.pagination.total - 1)
                if (Number(this.activeGroup?.id) === Number(groupId)) this.activeGroup = null
                delete this.messages[groupId]
            },

            groupDeleted(payload) {
                this.removeGroup(payload?.group_id)
            },
        },
    })
}
